import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, signal } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { RegionService } from '../../../../woo_services.module/RegionService';
import { SystemService } from '../../../../woo_services.module/SystemService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { ProductFormat, Publisher, Region, wooId } from '../../../../woo_services.module/shared-types';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

@Component({
  selector: 'regions',
  templateUrl: './manage-regions.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageRegions implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;
  loading = signal<boolean>(true);
  isEditing = signal<boolean>(false);
  totalSharesHaveChanged = signal<boolean>(false);
  tableHeaders = {};
  publishers: Publisher[] = [];
  selectedPublisher: Publisher;
  productFormats: ProductFormat[];
  selectedProductFormat: ProductFormat;
  regionRows = signal<RegionRow[]>([]);
  regions = signal<Region[]>([]);
  regionsCopy: Region[] = [];
  shareOfTotal: number;
  regionSharesHaveChanged: boolean;
  shares: Record<wooId, number> = {};
  translateProductFormat: (args: any) => string;
  constructor(
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private regionService: RegionService,
    private modalService: ModalService,
    private systemService: SystemService,
    private translationService: TranslationService,
    private utilityService: ProductsPageUtilityService,
    private changeDetection: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.translateProductFormat = this.translationService.convertProductFormat;

    try {
      this.publishers = (await this.publisherService.getPublishers(ExtraPublisherField.enabled)).filter(
        (p) => p.enabled,
      );
      await this.getandBuilsRegionRows();
      this.regionsCopy = structuredClone(this.regions());
      this.productFormats = (await this.productFormatService.getProductFormats(ExtraProductFormatField.enabled)).filter(
        (f) => f.enabled,
      );
      this.filterProductFormats();
      this.buildTableHeaders();
      this.shareOfTotal = await this.systemService.getValue('regional_estimate_share_of_total');
      this.changeDetection.markForCheck();
    } finally {
      this.loading.set(false);
    }
  }

  async getandBuilsRegionRows(): Promise<void> {
    this.regions.set(await this.regionService.getRegions());
    this.buildRegionRows();
  }

  setEditing(value: boolean): void {
    this.isEditing.set(value);
  }

  async cancelEditing(): Promise<void> {
    this.setEditing(false);
    await this.getandBuilsRegionRows();
  }

  async save(): Promise<void> {
    const filterActive = this.selectedPublisher || this.selectedProductFormat;
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade rader sparas.');
    }
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then(async (status) => {
        if (status.confirm) {
          const loaderModal = this.modalService.openLoader('Sparar ändringar');
          this.loading.set(true);

          this.regionService
            .updateRegionsShares(this.regions())
            .then(async () => {
              try {
                await this.getandBuilsRegionRows();
                this.totalSharesHaveChanged.set(true);
              } catch (err) {
                this.modalService.openError(
                  'Ajdå, något gick snett. Kunde inte spara ändringar.',
                  `${this.translationService.convertError(
                    err.error.error,
                  )}\n${this.translationService.convertDynamicValidationErrors(err.error)}`,
                );
              }
            })
            .catch(async (err) => {
              loaderModal.close();
              this.regions.set(structuredClone(this.regionsCopy));
              this.modalService.openError(
                'Ajdå, något gick snett. Kunde inte spara ändringar.',
                this.translationService.convertDynamicValidationErrors(err.error),
              );
              this.changeDetection.markForCheck();
            })
            .finally(() => {
              loaderModal.close();
              this.loading.set(false);
              this.setEditing(false);
              this.getandBuilsRegionRows();
              this.changeDetection.markForCheck();
            });
        }
      });
  }

  async updateShareOfTotal(): Promise<void> {
    try {
      await this.systemService.updateShareOfTotal({ regional_estimate_share_of_total: this.shareOfTotal });
      this.totalSharesHaveChanged.set(true);
    } catch (regions) {
      console.error('Error', regions);
      this.modalService.openError('Uppdateringen misslyckades');
    }
  }

  getRegion(regionId: wooId): Region {
    return this.regions().find((region) => region.id === regionId);
  }

  sumOfProductFormatShares(productFormatId: wooId): number {
    // js Math does not round decimal places
    return Math.round(this.regions().reduce((a, b) => a + (b.shares[productFormatId] || 0), 0) * 100 * 100) / 100;
  }

  rowVisible(regionId: wooId): boolean {
    return this.getRegion(regionId).enabled;
  }

  colVisible(productFormatId: wooId): boolean {
    if (!this.productFormats?.length) return true;

    if (this.selectedProductFormat && productFormatId !== this.selectedProductFormat.id) {
      return false;
    }
    return this.productFormats.some((format) => format.id === productFormatId);
  }

  private buildTableHeaders(): void {
    const tableHeaders = {};
    this.productFormats.map((productFormat) => {
      tableHeaders[productFormat.id] = productFormat.name;
    });
    this.tableHeaders = tableHeaders;
  }

  private buildRegionRows(): void {
    let regionRows: RegionRow[] = [];

    this.regions().map((region) => {
      regionRows = [
        ...regionRows,
        {
          regionName: region.name,
          regionId: region.id,
          productFormatIds: Object.keys(region.shares),
        },
      ];
    });
    this.regionRows.set(regionRows);
  }

  private filterProductFormats(): void {
    this.productFormats = this.productFormats.filter((format) =>
      this.publishers.some((publisher) => publisher.id === format.publisher_id),
    );
  }
}

interface RegionRow {
  regionName: string;
  regionId: wooId;
  productFormatIds: wooId[];
}
